import { memo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { T, useT } from '../../../translation/src';
import { ReferralOverviewFieldsFragment, ReferredOrganisationStatus } from '../../../types/graphqlTypes';
import { formatPrice } from '../../../utils/formatPrice';

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

interface ReferredOrganisationsListProps {
  organisations: ReferralOverviewFieldsFragment['referredOrganisations'];
  // A partner's referrals are its customers: named so, with the customer center to manage them.
  partner?: boolean;
}
// A reseller's or partner's list: who signed up through the link, and what each brought in.
const ReferredOrganisationsList_: React.FC<ReferredOrganisationsListProps> = ({ organisations, partner }) => {
  const referredTitle = useT('referred organisations', { swc: true });
  const customersTitle = useT('customers', { swc: true });
  const { formatDay } = useFormatDate();
  const navigate = useNavigate();
  const { organisationId = '' } = useParams();
  const onManage = useCallback(() => navigate(`/organisations/${organisationId}/referrals/customers`), [navigate, organisationId]);
  return (
    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#242424]">
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <span className="text-base font-semibold">{partner ? customersTitle : referredTitle}</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">{organisations.length}</span>
        </div>
        {partner && (
          <Button variant="secondary" size="xs" onClick={onManage}>
            <span className="whitespace-nowrap">
              <T _str="manage" swc />
            </span>
          </Button>
        )}
      </div>
      {organisations.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-gray-500">
          {partner ? <T _str="No customers yet. Add one from the customer center, or share your link." /> : <T _str="Nobody has signed up through your link yet. Share it and they will show up here." />}
        </div>
      ) : (
        <div className="flex max-h-[28rem] flex-col divide-y divide-gray-100 overflow-y-auto dark:divide-gray-700">
          {organisations.map((organisation) => {
            const active = organisation.status === ReferredOrganisationStatus.HasSales;
            return (
              <div key={organisation.id} className="flex items-center gap-4 px-6 py-3 text-sm">
                <span
                  className={
                    active
                      ? 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neon-green-300 text-sm font-bold text-eerie-black'
                      : 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-bold text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }
                >
                  {initialsOf(organisation.name)}
                </span>
                <div className="flex min-w-0 grow flex-col">
                  <span className="truncate font-semibold">{organisation.name}</span>
                  <span className="text-xs text-gray-500">
                    <T _str="joined" swc /> {formatDay(organisation.joinedAt)}
                  </span>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-0.5">
                  <span
                    className={
                      active
                        ? 'rounded-full bg-neon-green-200 px-2.5 py-0.5 text-xs font-semibold text-dark-green-800 dark:bg-neon-green-900/40 dark:text-neon-green-300'
                        : 'rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-500 dark:bg-gray-800'
                    }
                  >
                    {partner ? (
                      active ? (
                        <T _str="{count} orders" args={{ count: organisation.salesCount }} />
                      ) : (
                        <T _str="no order yet" />
                      )
                    ) : active ? (
                      <T _str="{count} sales" args={{ count: organisation.salesCount }} />
                    ) : (
                      <T _str="no sale yet" />
                    )}
                  </span>
                  {/* Sold, but the invoice is not paid yet: counted, and said so. */}
                  {organisation.awaitingPaymentCount > 0 && (
                    <span className="text-xs text-gray-500">
                      <T _str="{count} awaiting payment" args={{ count: organisation.awaitingPaymentCount }} />
                    </span>
                  )}
                </div>
                <div className="flex w-36 shrink-0 flex-col items-end">
                  <span className="font-semibold">{formatPrice(organisation.salesAmountCents)}</span>
                  <span className="text-xs text-gray-500">
                    {partner ? <T _str="discount" swc /> : <T _str="your commission" swc />}: {formatPrice(organisation.cutCents)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
export const ReferredOrganisationsList = memo(ReferredOrganisationsList_);
